import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiClientError, apiClient, getSession, type AuthSession } from '../api'

type UseAuthSessionOptions = {
  onAuthError?: () => void
}

export function useAuthSession({ onAuthError }: UseAuthSessionOptions = {}) {
  const [authSession, setAuthSession] = useState<AuthSession | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [authResolved, setAuthResolved] = useState(false)
  const [authError, setAuthError] = useState('')
  const [phoneLoginOpen, setPhoneLoginOpen] = useState(false)
  const [ownerGeneration, setOwnerGeneration] = useState(0)
  const authRequestRef = useRef(0)
  const authGenerationRef = useRef(0)
  const activeUserIdRef = useRef<string | null>(null)
  const authResolvedRef = useRef(false)
  const authSessionRef = useRef<AuthSession | null>(null)

  const activeUserId = authSession?.user?.id || null
  activeUserIdRef.current = activeUserId
  authResolvedRef.current = authResolved
  authSessionRef.current = authSession

  const bumpOwnerGeneration = useCallback(() => {
    authGenerationRef.current += 1
    setOwnerGeneration(current => current + 1)
    return authGenerationRef.current
  }, [])

  const isCurrentOwner = useCallback((generation: number, userId: string | null): boolean => (
    authResolvedRef.current
      && generation === authGenerationRef.current
      && userId === activeUserIdRef.current
  ), [])

  const invalidateOwner = useCallback(() => {
    bumpOwnerGeneration()
  }, [bumpOwnerGeneration])

  const moveToGuestAfterAuthFailure = useCallback((message: string, auth = authSessionRef.current?.auth) => {
    // A failed logout/session request must invalidate every older async
    // callback before exposing the guest projection. Keep provider metadata so
    // the login action remains available after an outage.
    authRequestRef.current += 1
    invalidateOwner()
    setAuthSession({
      user: null,
      csrfToken: null,
      auth: auth || { provider: 'casdoor', configured: false }
    })
    setAuthResolved(true)
    setAuthLoading(false)
    setAuthError(message)
  }, [invalidateOwner])

  const refreshAuth = useCallback(async () => {
    const request = ++authRequestRef.current
    setAuthLoading(true)
    try {
      const session = await getSession()
      if (request !== authRequestRef.current) return null
      if ((session.user?.id || null) !== activeUserIdRef.current) bumpOwnerGeneration()
      setAuthSession(session)
      setAuthResolved(true)
      setAuthError('')
      return session
    } catch (error) {
      if (request === authRequestRef.current) {
        // The identity is no longer trustworthy. Continue locally as a guest.
        moveToGuestAfterAuthFailure(error instanceof Error ? error.message : '账号服务暂时不可用')
      }
      return null
    } finally {
      if (request === authRequestRef.current) setAuthLoading(false)
    }
  }, [bumpOwnerGeneration, moveToGuestAfterAuthFailure])

  const loginWithCasdoor = useCallback(() => {
    if (!authSessionRef.current?.auth.configured) {
      setAuthError(authLoading ? '正在连接账号服务，请稍后再试。' : '登录服务尚未配置，请稍后重试。')
      return
    }
    setAuthError('')
    setPhoneLoginOpen(true)
  }, [authLoading])

  const closePhoneLogin = useCallback(() => setPhoneLoginOpen(false), [])

  const completePhoneLogin = useCallback(async () => {
    const session = await refreshAuth()
    if (!session?.user) throw new ApiClientError('手机号验证完成，但账号会话没有建立，请重试。', { code: 'AUTH_SESSION_MISSING' })
    setPhoneLoginOpen(false)
  }, [refreshAuth])

  const logoutFromCasdoor = useCallback(async (hasPendingCloud = false) => {
    const session = authSessionRef.current
    if (!session?.user) return
    if (hasPendingCloud && !window.confirm('当前还有未处理的云存档状态。退出后会保留本机进度，但不会替你完成云端选择，仍要退出吗？')) return
    setAuthError('')
    setAuthLoading(true)
    try {
      if (!session.csrfToken) throw new ApiClientError('当前登录会话不完整，无法退出，请刷新后重试', { code: 'CSRF_REQUIRED' })
      await apiClient.logout()
      // Clear the old owner before asking for the post-logout session.
      moveToGuestAfterAuthFailure('', session.auth)
      await refreshAuth()
    } catch (error) {
      // Logout failure is not permission to keep presenting the old identity.
      moveToGuestAfterAuthFailure(error instanceof Error ? error.message : '退出登录失败，已切换到游客模式。', session.auth)
    } finally {
      setAuthLoading(false)
    }
  }, [moveToGuestAfterAuthFailure, refreshAuth])

  useEffect(() => {
    void refreshAuth()
    const authQuery = new URLSearchParams(window.location.search).get('authError')
    if (authQuery) {
      setAuthError(authQuery === 'casdoor_auth_failed' ? '登录没有完成，请重试。' : '账号登录失败，请重试。')
      const cleanUrl = `${window.location.pathname}${window.location.hash}`
      window.history.replaceState({}, document.title, cleanUrl)
      onAuthError?.()
    }
  }, [onAuthError, refreshAuth])

  return {
    authSession,
    activeUserId,
    authLoading,
    authResolved,
    authError,
    ownerGeneration,
    refreshAuth,
    loginWithCasdoor,
    logoutFromCasdoor,
    isCurrentOwner,
    invalidateOwner,
    phoneLoginOpen,
    closePhoneLogin,
    completePhoneLogin
  }
}
